import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useFormik } from 'formik';
import { useCreateLoanRequestMutation } from '../../services/loanApi';
import { Card, Button, Input, Select } from '../../components/common';
import {
  ArrowLeftIcon,
  CheckCircleIcon,
  BanknotesIcon,
  CalculatorIcon,
  ClockIcon,
  CurrencyRupeeIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import socketService from '../../services/socketService';

const LoanRequestForm = ({ onSuccess }) => {
  const { t } = useTranslation(['common', 'loans']);
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);
  const [createLoanRequest, { isLoading }] = useCreateLoanRequestMutation();

  const [repaymentType, setRepaymentType] = useState('full_payment');
  const [emiCalculation, setEmiCalculation] = useState(null);
  const [showCalculator, setShowCalculator] = useState(false);
  const [applyInterest, setApplyInterest] = useState(false);

  // EMI Calculation function
  const calculateEMI = (principal, rate, tenure) => {
    if (!principal || !rate || !tenure) return null;

    const monthlyRate = rate / 12 / 100;
    const emi = (principal * monthlyRate * Math.pow(1 + monthlyRate, tenure)) /
                (Math.pow(1 + monthlyRate, tenure) - 1);

    const totalAmount = emi * tenure;
    const totalInterest = totalAmount - principal;

    return {
      monthlyEMI: Math.round(emi),
      totalAmount: Math.round(totalAmount),
      totalInterest: Math.round(totalInterest)
    };
  };

  // Dynamic validation function
  const validateForm = (values) => {
    const errors = {};

    // Loan type validation
    if (!values.loanType) {
      errors.loanType = t('loans:loanTypeRequired');
    }

    // Dynamic amount validation based on loan type
    let minAmount = 1000;
    let maxAmount = 500000;

    switch (values.loanType) {
      case 'salary_advance':
        maxAmount = 50000; // Max 50K for salary advance
        break;
      case 'personal_loan':
        minAmount = 10000; // Min 10K for personal loan
        maxAmount = 200000; // Max 2L for personal loan
        break;
      case 'emergency_loan':
        maxAmount = 100000; // Max 1L for emergency loan
        break;
    }

    if (!values.amount) {
      errors.amount = t('loans:amountRequired');
    } else if (values.amount < minAmount) {
      errors.amount = t('loans:minimumAmount', { amount: minAmount });
    } else if (values.amount > maxAmount) {
      errors.amount = t('loans:maximumAmount', { amount: maxAmount });
    }

    // Purpose validation
    if (!values.purpose) {
      errors.purpose = t('loans:purposeRequired');
    } else if (values.purpose.length < 10) {
      errors.purpose = t('loans:purposeMinLength');
    } else if (values.purpose.length > 500) {
      errors.purpose = t('loans:purposeMaxLength');
    }

    // Repayment type validation
    if (!values.repaymentType) {
      errors.repaymentType = t('loans:repaymentTypeRequired');
    }

    // EMI details validation
    if (values.repaymentType === 'emi_installments') {
      if (!values.emiDetails?.numberOfInstallments) {
        errors.emiDetails = errors.emiDetails || {};
        errors.emiDetails.numberOfInstallments = t('loans:numberOfInstallmentsRequired');
      } else if (values.emiDetails.numberOfInstallments < 3) {
        errors.emiDetails = errors.emiDetails || {};
        errors.emiDetails.numberOfInstallments = t('loans:minimumInstallments');
      } else if (values.emiDetails.numberOfInstallments > 60) {
        errors.emiDetails = errors.emiDetails || {};
        errors.emiDetails.numberOfInstallments = t('loans:maximumInstallments');
      }
    }

    // Interest rate validation
    if (values.applyInterest) {
      if (!values.interestRate) {
        errors.interestRate = t('loans:interestRateRequired');
      } else if (values.interestRate < 0 || values.interestRate > 30) {
        errors.interestRate = t('loans:interestRateRange');
      }
    }

    return errors;
  };

  const formik = useFormik({
    initialValues: {
      loanType: '',
      amount: '',
      purpose: '',
      repaymentType: 'full_payment',
      emiDetails: {
        numberOfInstallments: ''
      },
      applyInterest: false,
      interestRate: ''
    },
    validate: validateForm,
    onSubmit: async (values) => {
      try {
        const loanData = {
          ...values,
          amount: parseFloat(values.amount),
          emiDetails: values.repaymentType === 'emi_installments' ? {
            numberOfInstallments: parseInt(values.emiDetails.numberOfInstallments)
          } : undefined,
          applyInterest: values.applyInterest,
          interestRate: values.applyInterest ? parseFloat(values.interestRate) : undefined
        };

        await createLoanRequest(loanData).unwrap();

        toast.success(t('loans:requestSubmittedSuccessfully'));

        // Reset form
        formik.resetForm();
        setRepaymentType('full_payment');
        setEmiCalculation(null);
        setShowCalculator(false);
        setApplyInterest(false);

        // Close modal
        if (onSuccess) {
          onSuccess();
        }
      } catch (error) {
        console.error('Error creating loan request:', error);
        toast.error(error.data?.message || t('loans:requestSubmissionFailed'));
      }
    }
  });

  // Update validation when loan type or repayment type changes
  useEffect(() => {
    // Trigger re-validation when loan type or repayment type changes
    if (formik.values.loanType || formik.values.repaymentType) {
      formik.validateForm();
    }
  }, [formik.values.loanType, formik.values.repaymentType]);

  // Calculate EMI when amount or installments change
  useEffect(() => {
    if (repaymentType === 'emi_installments' &&
        formik.values.amount &&
        formik.values.emiDetails.numberOfInstallments) {
      // Use interest rate from form or default to 0 if no interest applied
      const interestRate = formik.values.applyInterest ? (formik.values.interestRate || 0) : 0;
      const calculation = calculateEMI(
        parseFloat(formik.values.amount),
        interestRate,
        parseInt(formik.values.emiDetails.numberOfInstallments)
      );
      setEmiCalculation(calculation);
    } else {
      setEmiCalculation(null);
    }
  }, [formik.values.amount, formik.values.emiDetails.numberOfInstallments, formik.values.applyInterest, formik.values.interestRate, repaymentType]);

  const handleRepaymentTypeChange = (value) => {
    setRepaymentType(value);
    formik.setFieldValue('repaymentType', value);
    if (value === 'full_payment') {
      setEmiCalculation(null);
      setShowCalculator(false);
    }
  };

  const handleLoanTypeChange = (value) => {
    formik.setFieldValue('loanType', value);
    // Reset amount when loan type changes to trigger new validation
    formik.setFieldValue('amount', '');
  };

  const loanTypeOptions = [
    { value: 'salary_advance', label: t('loans:salaryAdvance'), description: t('loans:salaryAdvanceDesc') || 'Advance against your salary', maxAmount: 50000 },
    { value: 'personal_loan', label: t('loans:personalLoan'), description: t('loans:personalLoanDesc') || 'Loan for personal needs', minAmount: 10000, maxAmount: 200000 },
    { value: 'emergency_loan', label: t('loans:emergencyLoan'), description: t('loans:emergencyLoanDesc') || 'Quick loan for emergencies', maxAmount: 100000 }
  ];

  const repaymentTypeOptions = [
    { value: 'full_payment', label: t('loans:fullPayment'), description: t('loans:fullPaymentDesc') || 'Pay the entire amount at once' },
    { value: 'emi_installments', label: t('loans:emiInstallments'), description: t('loans:emiInstallmentsDesc') || 'Pay in monthly installments' }
  ];

  const selectedLoanType = loanTypeOptions.find(type => type.value === formik.values.loanType);

  return (
    <div className="max-w-6xl mx-auto p-2">
      {/* Compact Modal Header */}
      <div className="flex items-center justify-between mb-4 pb-3 border-b border-gray-200">
        <div className="flex items-center space-x-2">
          <div className="p-1.5 bg-blue-100 rounded-md">
            <BanknotesIcon className="h-5 w-5 text-blue-600" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-gray-900">{t('loans:requestLoan')}</h2>
            <p className="text-xs text-gray-600">{t('loans:requestLoanDescription')}</p>
          </div>
        </div>
      </div>

    

      {/* Form Content */}
      <form onSubmit={formik.handleSubmit} className="space-y-4">
        {/* Loan Type and Amount Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Loan Type Selection */}
          <div className="space-y-3">
            <div className="flex items-center space-x-2">
              <BanknotesIcon className="h-4 w-4 text-blue-600" />
              <label className="text-base font-semibold text-gray-800">
                {t('loans:loanType')} <span className="text-red-500">*</span>
              </label>
            </div>

            <div className="grid grid-cols-1 gap-2">
              {loanTypeOptions.map((type) => (
                <div
                  key={type.value}
                  onClick={() => handleLoanTypeChange(type.value)}
                  className={`p-3 rounded-lg border-2 cursor-pointer transition-all duration-200 hover:shadow-sm ${
                    formik.values.loanType === type.value
                      ? 'border-blue-500 bg-blue-50 shadow-sm'
                      : 'border-gray-200 bg-white hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center space-x-2">
                    <div className={`w-3 h-3 rounded-full border-2 ${
                      formik.values.loanType === type.value
                        ? 'border-blue-500 bg-blue-500'
                        : 'border-gray-300'
                    }`}>
                      {formik.values.loanType === type.value && (
                        <div className="w-full h-full rounded-full bg-white scale-50"></div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-medium text-gray-900 text-sm">{type.label}</h3>
                      <p className="text-xs text-gray-500 truncate">
                        {type.minAmount && `₹${type.minAmount.toLocaleString()}`}
                        {type.minAmount && type.maxAmount && ' - '}
                        {type.maxAmount && `₹${type.maxAmount.toLocaleString()}`}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {formik.touched.loanType && formik.errors.loanType && (
              <div className="flex items-center space-x-1 text-red-600 bg-red-50 px-3 py-2 rounded-md border border-red-200">
                <ExclamationTriangleIcon className="h-4 w-4" />
                <span className="text-xs">{formik.errors.loanType}</span>
              </div>
            )}
          </div>

          {/* Amount Input */}
          <div className="space-y-3">
            <div className="flex items-center space-x-2">
              <CurrencyRupeeIcon className="h-4 w-4 text-green-600" />
              <label className="text-base font-semibold text-gray-800">
                {t('loans:loanAmount')} <span className="text-red-500">*</span>
              </label>
            </div>

            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <span className="text-gray-500 text-sm font-semibold">₹</span>
              </div>
              <Input
                type="number"
                name="amount"
                value={formik.values.amount}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                placeholder={t('loans:enterAmount')}
                error={formik.touched.amount && formik.errors.amount}
                className="pl-8 text-sm py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white/70 backdrop-blur-sm transition-all duration-200 hover:border-gray-300"
                min={selectedLoanType?.minAmount || 1000}
                max={selectedLoanType?.maxAmount || 500000}
              />
            </div>

            {selectedLoanType && (
              <div className="bg-blue-50 border border-blue-200 rounded-md p-2">
                <div className="flex items-center space-x-1 text-blue-800">
                  <ClockIcon className="h-4 w-4" />
                  <span className="font-medium text-xs">Range for {selectedLoanType.label}</span>
                </div>
                <p className="text-xs text-blue-700 mt-1">
                  ₹{(selectedLoanType.minAmount || 1000).toLocaleString()} - ₹{(selectedLoanType.maxAmount || 500000).toLocaleString()}
                </p>
              </div>
            )}

            {formik.touched.amount && formik.errors.amount && (
              <div className="flex items-center space-x-1 text-red-600 bg-red-50 px-3 py-2 rounded-md border border-red-200">
                <ExclamationTriangleIcon className="h-4 w-4" />
                <span className="text-xs">{formik.errors.amount}</span>
              </div>
            )}
          </div>
        </div>

        {/* Repayment Type and Interest Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Repayment Type Selection */}
          <div className="space-y-3">
            <div className="flex items-center space-x-2">
              <CalculatorIcon className="h-4 w-4 text-purple-600" />
              <label className="text-base font-semibold text-gray-800">
                {t('loans:repaymentType')} <span className="text-red-500">*</span>
              </label>
            </div>

            <div className="grid grid-cols-1 gap-2">
              {repaymentTypeOptions.map((type) => (
                <div
                  key={type.value}
                  onClick={() => handleRepaymentTypeChange(type.value)}
                  className={`p-3 rounded-lg border-2 cursor-pointer transition-all duration-200 hover:shadow-sm ${
                    repaymentType === type.value
                      ? 'border-purple-500 bg-purple-50 shadow-sm'
                      : 'border-gray-200 bg-white hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center space-x-2">
                    <div className={`w-3 h-3 rounded-full border-2 ${
                      repaymentType === type.value
                        ? 'border-purple-500 bg-purple-500'
                        : 'border-gray-300'
                    }`}>
                      {repaymentType === type.value && (
                        <div className="w-full h-full rounded-full bg-white scale-50"></div>
                      )}
                    </div>
                    <div className="flex-1">
                      <h3 className="font-medium text-gray-900 text-sm">{type.label}</h3>
                      <p className="text-xs text-gray-600">{type.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {formik.touched.repaymentType && formik.errors.repaymentType && (
              <div className="flex items-center space-x-1 text-red-600 bg-red-50 px-3 py-2 rounded-md border border-red-200">
                <ExclamationTriangleIcon className="h-4 w-4" />
                <span className="text-xs">{formik.errors.repaymentType}</span>
              </div>
            )}
          </div>

          {/* Interest Application */}
          <div className="space-y-3">
            <div className="flex items-center space-x-2">
              <CalculatorIcon className="h-4 w-4 text-amber-600" />
              <label className="text-base font-semibold text-gray-800">
                {t('loans:applyInterest')}
              </label>
            </div>

            <div className="space-y-3">
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formik.values.applyInterest}
                  onChange={(e) => {
                    formik.setFieldValue('applyInterest', e.target.checked);
                    if (!e.target.checked) {
                      formik.setFieldValue('interestRate', '');
                    }
                  }}
                  className="w-4 h-4 text-amber-600 bg-gray-100 border-gray-300 rounded focus:ring-amber-500 focus:ring-2"
                />
                <span className="text-sm font-medium text-gray-700">
                  {formik.values.applyInterest ? t('loans:interestApplied', { rate: formik.values.interestRate || 0 }) : t('loans:noInterest')}
                </span>
              </label>

              {formik.values.applyInterest && (
                <div>
                  <label className="block text-sm font-semibold text-gray-800 mb-1">
                    {t('loans:interestRate')} <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <Input
                      type="number"
                      name="interestRate"
                      value={formik.values.interestRate}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      placeholder={t('loans:enterInterestRate')}
                      error={formik.touched.interestRate && formik.errors.interestRate}
                      className="text-sm py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent bg-white/70 backdrop-blur-sm transition-all duration-200 hover:border-gray-300 pr-8"
                      min="0"
                      max="30"
                      step="0.1"
                    />
                    <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                      <span className="text-gray-500 text-xs font-medium">%</span>
                    </div>
                  </div>
                  {formik.touched.interestRate && formik.errors.interestRate && (
                    <div className="flex items-center space-x-1 text-red-600 bg-red-50 px-3 py-2 rounded-md border border-red-200 mt-1">
                      <ExclamationTriangleIcon className="h-4 w-4" />
                      <span className="text-xs">{formik.errors.interestRate}</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

                {/* EMI Calculator */}
                {repaymentType === 'emi_installments' && (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <CalculatorIcon className="h-4 w-4 text-orange-600" />
                        <label className="text-base font-semibold text-gray-800">
                          {t('loans:numberOfInstallments')} <span className="text-red-500">*</span>
                        </label>
                      </div>
                      <button
                        type="button"
                        onClick={() => setShowCalculator(!showCalculator)}
                        className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                      >
                        {showCalculator ? 'Hide' : 'Show'} Calculator
                      </button>
                    </div>

                    <div className="relative">
                      <Input
                        type="number"
                        name="emiDetails.numberOfInstallments"
                        value={formik.values.emiDetails.numberOfInstallments}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        placeholder={t('loans:enterNumberOfInstallments')}
                        error={
                          formik.touched.emiDetails?.numberOfInstallments &&
                          formik.errors.emiDetails?.numberOfInstallments
                        }
                        className="text-sm py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent bg-white/70 backdrop-blur-sm transition-all duration-200 hover:border-gray-300"
                        min="3"
                        max="60"
                      />
                    </div>

                    {showCalculator && emiCalculation && (
                      <div className="bg-gradient-to-r from-orange-50 to-yellow-50 border border-orange-200 rounded-lg p-4">
                        <h4 className="font-semibold text-gray-900 mb-3 flex items-center text-sm">
                          <CalculatorIcon className="h-4 w-4 mr-2 text-orange-600" />
                          EMI Calculation ({formik.values.applyInterest ? `${formik.values.interestRate || 0}% p.a.` : 'No Interest'})
                        </h4>
                        <div className="grid grid-cols-3 gap-3">
                          <div className="text-center">
                            <p className="text-lg font-bold text-orange-600">₹{emiCalculation.monthlyEMI.toLocaleString()}</p>
                            <p className="text-xs text-gray-600">Monthly EMI</p>
                          </div>
                          <div className="text-center">
                            <p className="text-lg font-bold text-green-600">₹{emiCalculation.totalAmount.toLocaleString()}</p>
                            <p className="text-xs text-gray-600">Total Amount</p>
                          </div>
                          <div className="text-center">
                            <p className="text-lg font-bold text-red-600">₹{emiCalculation.totalInterest.toLocaleString()}</p>
                            <p className="text-xs text-gray-600">Total Interest</p>
                          </div>
                        </div>
                        <div className="mt-3 p-2 bg-white/50 rounded-md">
                          <p className="text-xs text-gray-600">
                            *Estimated calculation. Actual EMI may vary.
                          </p>
                        </div>
                      </div>
                    )}

                    <div className="bg-blue-50 border border-blue-200 rounded-md p-2">
                      <div className="flex items-center space-x-1 text-blue-800">
                        <ClockIcon className="h-4 w-4" />
                        <span className="font-medium text-xs">Installment Range</span>
                      </div>
                      <p className="text-xs text-blue-700 mt-1">
                        3 - 60 months (5 years)
                      </p>
                    </div>

                    {formik.touched.emiDetails?.numberOfInstallments &&
                     formik.errors.emiDetails?.numberOfInstallments && (
                      <div className="flex items-center space-x-1 text-red-600 bg-red-50 px-3 py-2 rounded-md border border-red-200">
                        <ExclamationTriangleIcon className="h-4 w-4" />
                        <span className="text-xs">{formik.errors.emiDetails.numberOfInstallments}</span>
                      </div>
                    )}
                  </div>
                )}

                {/* Purpose */}
                <div className="space-y-3">
                  <div className="flex items-center space-x-2">
                    <DocumentTextIcon className="h-4 w-4 text-indigo-600" />
                    <label className="text-base font-semibold text-gray-800">
                      {t('loans:purpose')} <span className="text-red-500">*</span>
                    </label>
                  </div>

                  <textarea
                    name="purpose"
                    value={formik.values.purpose}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    rows={3}
                    placeholder={t('loans:enterPurpose')}
                    className={`w-full px-3 py-3 border-2 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white/70 backdrop-blur-sm transition-all duration-200 hover:border-gray-300 resize-none text-sm ${
                      formik.touched.purpose && formik.errors.purpose
                        ? 'border-red-300 focus:border-red-500'
                        : 'border-gray-200 focus:border-indigo-500'
                    }`}
                  />

                  <div className="flex justify-between items-center text-xs text-gray-600">
                    <span>{formik.values.purpose.length}/500</span>
                    <span>Min 10 characters</span>
                  </div>

                  {formik.touched.purpose && formik.errors.purpose && (
                    <div className="flex items-center space-x-1 text-red-600 bg-red-50 px-3 py-2 rounded-md border border-red-200">
                      <ExclamationTriangleIcon className="h-4 w-4" />
                      <span className="text-xs">{formik.errors.purpose}</span>
                    </div>
                  )}
                </div>

        {/* Form Actions */}
        <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-gray-200">
          <button
            type="submit"
            disabled={isLoading || !formik.isValid}
            className="flex-1 px-4 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg hover:from-blue-700 hover:to-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transform hover:scale-105 transition-all duration-200 shadow-lg hover:shadow-xl flex items-center justify-center text-sm font-semibold"
          >
            {isLoading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                {t('loans:submitRequest')}
              </>
            ) : (
              <>
                <CheckCircleIcon className="h-4 w-4 mr-2" />
                {t('loans:submitRequest')}
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default LoanRequestForm;